import { tick, FocusMsg, BlurMsg } from '@boba-cli/tea';
import { Style } from '@boba-cli/chapstick';

// src/model.ts

// src/messages.ts
var InitialBlinkMsg = class {
  _tag = "cursor:init-blink";
};
var BlinkMsg = class {
  constructor(id, tag, time) {
    this.id = id;
    this.tag = tag;
    this.time = time;
  }
  _tag = "cursor:blink";
};

// src/model.ts
var defaultBlinkSpeed = 530;
var lastId = 0;
function nextId() {
  return ++lastId;
}
var CursorMode = /* @__PURE__ */ ((CursorMode2) => {
  CursorMode2["Blink"] = "blink";
  CursorMode2["Static"] = "static";
  CursorMode2["Hidden"] = "hidden";
  return CursorMode2;
})(CursorMode || {});
var CursorModel = class _CursorModel {
  blinkSpeed;
  style;
  textStyle;
  char;
  #id;
  #tag;
  #blink;
  #focus;
  #mode;
  constructor(options = {}, state) {
    this.blinkSpeed = options.blinkSpeed ?? defaultBlinkSpeed;
    this.style = options.style ?? new Style();
    this.textStyle = options.textStyle ?? new Style();
    this.char = options.char ?? " ";
    this.#id = state?.id ?? nextId();
    this.#tag = state?.tag ?? 0;
    this.#blink = state?.blink ?? true;
    this.#focus = state?.focus ?? false;
    this.#mode = state?.mode ?? options.mode ?? "blink" /* Blink */;
  }
  /** Unique ID for this cursor (for message routing). */
  id() {
    return this.#id;
  }
  /** Current cursor mode. */
  mode() {
    return this.#mode;
  }
  /** Whether the cursor is currently in the "text" state (hidden block). */
  isBlinkHidden() {
    return this.#blink;
  }
  /** Whether this cursor currently has focus. */
  isFocused() {
    return this.#focus;
  }
  /** Kick off blinking at init (emits InitialBlinkMsg). */
  initBlink() {
    return () => new InitialBlinkMsg();
  }
  /** Command to schedule the next blink toggle. */
  tickBlink() {
    const nextTag = this.#tag + 1;
    const id = this.#id;
    const speed = this.blinkSpeed;
    const next = this.#withState({ ...this.#state(), tag: nextTag });
    const cmd = tick(
      speed,
      (time) => new BlinkMsg(id, nextTag, time)
    );
    return [next, cmd];
  }
  /** Set the character under the cursor. */
  withChar(char) {
    return this.#withState(this.#state(), { char });
  }
  /** Set the cursor mode. Returns a new model and optional blink command. */
  withMode(mode) {
    const bounded = mode === "blink" /* Blink */ || mode === "static" /* Static */ || mode === "hidden" /* Hidden */ ? mode : "blink" /* Blink */;
    const blink = bounded === "hidden" /* Hidden */ || !this.#focus ? true : this.#blink;
    const next = this.#withState({ ...this.#state(), blink, mode: bounded });
    if (bounded === "blink" /* Blink */ && this.#focus) {
      const [scheduled, cmd] = next.tickBlink();
      return [scheduled, cmd];
    }
    return [next, null];
  }
  /** Focus the cursor. Returns new model and optional blink command. */
  focus() {
    const blink = this.#mode === "hidden" /* Hidden */ ? true : this.#blink;
    const next = this.#withState({ ...this.#state(), blink, focus: true });
    if (this.#mode === "blink" /* Blink */) {
      const [scheduled, cmd] = next.tickBlink();
      return [scheduled, cmd];
    }
    return [next, null];
  }
  /** Blur the cursor. */
  blur() {
    return this.#withState({ ...this.#state(), blink: true, focus: false });
  }
  /**
   * Update the cursor model with an incoming message.
   * Returns a new model and an optional command.
   */
  update(msg) {
    if (msg instanceof InitialBlinkMsg) {
      if (this.#mode !== "blink" /* Blink */ || !this.#focus) {
        return [this, null];
      }
      const [next, cmd] = this.tickBlink();
      return [next, cmd];
    }
    if (msg instanceof FocusMsg) {
      return this.focus();
    }
    if (msg instanceof BlurMsg) {
      const next = this.blur();
      return [next, null];
    }
    if (msg instanceof BlinkMsg) {
      if (this.#mode !== "blink" /* Blink */ || !this.#focus) {
        return [this, null];
      }
      if (msg.id !== this.#id || msg.tag !== this.#tag) {
        return [this, null];
      }
      const toggled = this.#withState({ ...this.#state(), blink: !this.#blink });
      const [next, cmd] = toggled.tickBlink();
      return [next, cmd];
    }
    return [this, null];
  }
  /** Render the cursor. */
  view() {
    const char = this.char;
    if (this.#blink) {
      return this.textStyle.inline(true).render(char);
    }
    return this.style.inline(true).render(char);
  }
  #state() {
    return {
      id: this.#id,
      tag: this.#tag,
      blink: this.#blink,
      focus: this.#focus,
      mode: this.#mode
    };
  }
  #withState(state, overrides) {
    return new _CursorModel(
      {
        blinkSpeed: this.blinkSpeed,
        style: this.style,
        textStyle: this.textStyle,
        char: overrides?.char ?? this.char,
        mode: state.mode,
        focused: state.focus
      },
      state
    );
  }
};

export { BlinkMsg, CursorMode, CursorModel, InitialBlinkMsg };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map